import { SubscriptionPlans } from "@prisma/client";

import { MODEL_TRAINING_MAX_QUOTA } from "@musicfy/contants/Features";
import {
  FREE_SONG_DAILY,
  RETRAINS_PER_MODEL,
  getDailyFairUseCompactLabel,
  getMonthlySongQuota,
} from "@musicfy/contants/Quotas";

// Chart cells read from the enforced quotas so the copy cannot drift from them.
// Same compact form as getDailyFairUseCompactLabel, e.g. "200/day".
const perDay = (n: number): string => `${n.toLocaleString("en-US")}/day`;
const perMonth = (n: number): string => `${n.toLocaleString("en-US")}/month`;

export const VOICE_TRAINING_HEADERS = [
  "Voice Training",
  "Voices",
  "Quality",
  "Trainings per Voice",
  "Vocal Cleanup",
  "Training Speed",
  "Simultaneous Training",
  "Dataset Length",
];
export const VOICE_TRAINING_FEATURES = [
  {
    plan: SubscriptionPlans.free,
    voices: MODEL_TRAINING_MAX_QUOTA[SubscriptionPlans.free],
    quality: undefined,
    speed: undefined,
    simaltaneousTrains: undefined,
    trains: undefined,
    datasetLength: undefined,
    vocalEnhancement: undefined,
    color: "#FFFFFF",
  },
  {
    plan: SubscriptionPlans.starter,
    voices: MODEL_TRAINING_MAX_QUOTA[SubscriptionPlans.starter],
    quality: "High-Fidelity",
    speed: "Standard",
    simaltaneousTrains: undefined,
    datasetLength: "10 Minutes",
    trains: perMonth(RETRAINS_PER_MODEL[SubscriptionPlans.starter]),
    vocalEnhancement: false,
    color: "#FE7ED9",
  },
  {
    plan: SubscriptionPlans.pro,
    voices: MODEL_TRAINING_MAX_QUOTA[SubscriptionPlans.pro],
    quality: "High-Fidelity",
    speed: "Standard",
    simaltaneousTrains: undefined,
    datasetLength: "30 Minutes",
    trains: perMonth(RETRAINS_PER_MODEL[SubscriptionPlans.pro]),
    vocalEnhancement: true,
    color: "#FF95A9",
  },
  {
    plan: SubscriptionPlans.studio,
    voices: MODEL_TRAINING_MAX_QUOTA[SubscriptionPlans.studio],
    quality: "High-Fidelity",
    speed: "Fastest",
    simaltaneousTrains: true,
    datasetLength: "1 Hour",
    trains: perMonth(RETRAINS_PER_MODEL[SubscriptionPlans.studio]),
    vocalEnhancement: true,
    color: "#CE7EFF",
  },
];

export const AUDIO_GENERATION_HEADERS = [
  "Audio Generations",
  "Voice Cloning",
  "Song Generation",
  "Conversion Speed",
  "Remove Instrumentals",
  "Access to All Voices",
  "Enhance Vocals",
  "Master Quality",
];
export const AUDIO_GENERATION_FEATURES = [
  {
    plan: SubscriptionPlans.free,
    voiceCloning: getDailyFairUseCompactLabel(SubscriptionPlans.free),
    songGeneration: perDay(FREE_SONG_DAILY),
    speed: "Standard",
    voiceAccess: undefined,
    masterQuality: undefined,
    removeInstrumentals: undefined,
    enhanceVocals: undefined,
    color: "#FFFFFF",
  },
  {
    plan: SubscriptionPlans.starter,
    voiceCloning: getDailyFairUseCompactLabel(SubscriptionPlans.starter),
    songGeneration: perMonth(getMonthlySongQuota(SubscriptionPlans.starter)),
    speed: "Fast",
    voiceAccess: true,
    masterQuality: true,
    removeInstrumentals: getDailyFairUseCompactLabel(SubscriptionPlans.starter),
    enhanceVocals: true,
    color: "#FE7ED9",
  },
  {
    plan: SubscriptionPlans.pro,
    voiceCloning: getDailyFairUseCompactLabel(SubscriptionPlans.pro),
    songGeneration: perMonth(getMonthlySongQuota(SubscriptionPlans.pro)),
    speed: "Fast",
    voiceAccess: true,
    masterQuality: true,
    removeInstrumentals: getDailyFairUseCompactLabel(SubscriptionPlans.pro),
    enhanceVocals: true,
    color: "#FF95A9",
  },
  {
    plan: SubscriptionPlans.studio,
    voiceCloning: getDailyFairUseCompactLabel(SubscriptionPlans.studio),
    songGeneration: perMonth(getMonthlySongQuota(SubscriptionPlans.studio)),
    speed: "Fastest",
    voiceAccess: true,
    masterQuality: true,
    removeInstrumentals: getDailyFairUseCompactLabel(SubscriptionPlans.studio),
    enhanceVocals: true,
    color: "#CE7EFF",
  },
];

export const QUESTIONS = [
  {
    question: "How does Song Generation work?",
    answer:
      "Simply describe a mood, genre, or idea and Musicfy generates a Full Song with vocals and instrumentation; as well as shorter instrumental loops and backing beds. Write your own Lyrics, or let us handle it. On Pro and Studio plans, Voice Targeting lets the song be sung in your own trained voice.",
  },
  {
    question: "How long can a song be?",
    answer:
      "Songs with vocals can be between 45 seconds and 4 minutes; while instrumentals are between 10 seconds and 4 minutes for loops and backing beds.",
  },
  {
    question: "What is the difference between standard and master quality?",
    answer:
      "The main difference between standard and master quality is the pitch detection we use. Master quality will be far more accurate in converting the pitch between two voices. Visit <a style='text-decoration: underline;' href='https://musicfy.lol/standard-vs-master' target='_blank'>https://musicfy.lol/standard-vs-master</a> to view an example.",
  },
  {
    question: "What if I want to train my voice again?",
    answer:
      "If for whatever reason you are not satisfied with your voice, you can delete and train the voice again as many times as you like. The maximum number of voices you can have at once is determined by your plan.",
  },
  {
    question: "What does enhance vocals do?",
    answer:
      "Selected the 'Enhance Vocals' option will upsample the audio and remove any reverb or echo.",
  },
  {
    question: "Are my generations available for commercial use?",
    answer:
      "If your plan includes a commercial license you are free to use your generations for commercial use. You fully own the rights to your generations, as long as the voice you used was trained on copyright free data. All Musicfy models are trained on copyright free data.",
  },
  {
    question: "Does this work in every language?",
    answer:
      "Yes this works in every language. We do not convert your words, rather we convert the notes and pitch of the voice.",
  },
  {
    question: "How do I cancel my plan?",
    answer:
      "You can cancel your plan at any time and keep access until the end of your billing period. To manage your plan, visit account settings.",
  },
  {
    question: "Can I change my plan after I subscribe?",
    answer:
      "You can upgrade or downgrade your plan at any time by visiting account settings.",
  },
  {
    question: "Do you have a refund policy?",
    answer:
      "Visit <a style='text-decoration: underline;' href='https://musicfy.lol/refund' target='_blank'>https://musicfy.lol/refund</a> to view our refund policy.",
  },
];
